import { useAdminCancelReservation } from "@/api/admin/useAdminCancelReservation";
import type { EventVenueResponse } from "@/api/event/useGetEventVenues";
import {
  GetMyPersonalityTestAnswersDto,
  ReservationRequestDto,
} from "@/api/generated/generatedTypes";
import { ViewAsUserButton } from "@/components/ViewAs/ViewAsUserButton";
import { calculateAge } from "@/utils/date";
import { ActionIcon, Badge, Button, Card, Group, Stack, Text, Title } from "@mantine/core";
import { IconCirclePlus } from "@tabler/icons-react";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import relativeTime from "dayjs/plugin/relativeTime";
import { useState } from "react";
import type { ExistingEventWithStaging } from "../ExistingEventCard/types";
import { ReservationPreferencesSection } from "../ReservationPreferencesSection";
import { StagingGroup } from "../types";
import { UserEventHistory } from "../components/UserEventHistory";
import { showErrorNotification, showSuccessNotification } from "../utils/errorNotifications";

dayjs.extend(relativeTime);
dayjs.locale("ko");

interface PendingRequestCardProps {
  request: ReservationRequestDto;
  selectedStagingGroup: StagingGroup | null;
  selectedExistingEvent: ExistingEventWithStaging | null;
  venues: EventVenueResponse[];
  selectedQuestionIds: number[];
  onAddToGroup: (request: ReservationRequestDto) => void;
  onRequestCancelled: (requestId: number) => void;
}

const PersonalityAnswers = ({
  answers,
  selectedQuestionIds,
}: {
  answers: GetMyPersonalityTestAnswersDto[];
  selectedQuestionIds: number[];
}) => {
  if (!answers || answers.length === 0) {
    return <Text c="black">성격 테스트 답변을 찾을 수 없습니다.</Text>;
  }

  const filteredAnswers = answers.filter((answer) =>
    selectedQuestionIds.includes(answer.question.id)
  );

  if (filteredAnswers.length === 0) {
    return (
      <Text c="dimmed" size="sm">
        필터된 질문이 없습니다
      </Text>
    );
  }

  return (
    <Stack gap="xs">
      {filteredAnswers.map((answer) => (
        <Text c="black" size="sm" key={answer.id}>
          <Text span fw={700} c="black">
            {answer.question.title}:
          </Text>{" "}
          {answer.answerText ||
            answer.answerScale ||
            answer.selectedOptions.map((o) => o.option.displayText).join(", ")}
        </Text>
      ))}
    </Stack>
  );
};

export const PendingRequestCard = ({
  request,
  selectedStagingGroup,
  selectedExistingEvent,
  venues,
  selectedQuestionIds,
  onAddToGroup,
  onRequestCancelled,
}: PendingRequestCardProps) => {
  const [showAnswers, setShowAnswers] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [showEventHistory, setShowEventHistory] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const { mutateAsync: cancelReservation } = useAdminCancelReservation();
  const age = calculateAge(request.user.birthDate);
  const gender = request.user.gender || "정보없음";
  const genderMap = {
    MALE: "남성",
    FEMALE: "여성",
    NON_BINARY: "논바이너리",
  };
  const createdAt = dayjs(request.createdAt).format("M월 D일 HH:mm");

  const formatTime = (time: Date | string) => {
    const timeString = typeof time === "string" ? time : time.toISOString();
    return new Date(timeString).toLocaleString();
  };

  const handleCancel = async () => {
    if (
      !window.confirm(
        `${request.user.name}님의 대기 중인 예약 요청을 취소하시겠습니까? 환불이 가능한 경우 함께 처리되며, 이 작업은 되돌릴 수 없습니다.`
      )
    ) {
      return;
    }

    setIsCancelling(true);
    try {
      await cancelReservation(request.id);
      showSuccessNotification("예약 요청 취소 완료", "예약 요청이 성공적으로 취소되었습니다");
      onRequestCancelled(request.id);
    } catch (error) {
      showErrorNotification(error, "예약 요청 취소 실패");
    } finally {
      setIsCancelling(false);
    }
  };

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder pos="relative">
      <ActionIcon
        variant="subtle"
        color="green"
        onClick={() => onAddToGroup(request)}
        disabled={!selectedStagingGroup && !selectedExistingEvent}
        pos="absolute"
        top={9}
        right={9}
      >
        <IconCirclePlus size={24} />
      </ActionIcon>
      <Stack>
        <Group justify="space-between">
          <div>
            <Group gap="xs">
              <Title order={4} c="black">
                {request.user.name} ({request.user.nickname} / {age}살)
              </Title>
              {request.user.isTestUser && (
                <Badge size="sm" color="grape">
                  TEST
                </Badge>
              )}
              <ViewAsUserButton
                userId={request.user.uid}
                userName={request.user.name}
                isTestUser={request.user.isTestUser}
              />
            </Group>
            <Text c="dimmed" size="xs">
              created at: {createdAt}
            </Text>
          </div>
        </Group>

        <Group>
          <Text c="black" size="sm">
            <Text span fw={700}>
              성별:
            </Text>{" "}
            {genderMap[gender as keyof typeof genderMap]}
          </Text>
          <Text c="black" size="sm">
            <Text span fw={700}>
              나이:
            </Text>{" "}
            {age}
          </Text>
          <Text c="black" size="sm">
            <Text span fw={700}>
              직업:
            </Text>{" "}
            {request.user.profession}
          </Text>
        </Group>

        <Group>
          <Text c="black" size="sm">
            <Text span fw={700}>
              그룹 성별 선호:
            </Text>{" "}
            {request.user.groupGenderPreference === "ALL_GENDERS_OKAY"
              ? "모든 성별 괜찮아요"
              : request.user.groupGenderPreference === "SAME_GENDER_PREFERRED"
                ? "동성 선호"
                : "미설정"}
          </Text>
          <Text c="black" size="sm">
            <Text span fw={700}>
              그룹 연령 선호:
            </Text>{" "}
            {request.user.groupAgePreference === "ALL_AGES_OKAY"
              ? "모든 연령 괜찮아요"
              : request.user.groupAgePreference === "SAME_AGE_PREFERRED"
                ? "비슷한 연령 선호"
                : "미설정"}
          </Text>
        </Group>

        <Stack gap="xs">
          <Text c="black" size="sm" fw={700}>
            선호 시간:
          </Text>
          {request.preferredTimes.length > 0 ? (
            <Stack gap="xs" pl="md">
              {request.preferredTimes.map((time, index) => {
                const timeString = typeof time === "string" ? time : (time as Date).toISOString();
                const isTimeMatch =
                  timeString === selectedStagingGroup?.selectedDate ||
                  (selectedExistingEvent &&
                    new Date(timeString).toISOString() ===
                      new Date(selectedExistingEvent.startTime).toISOString());
                return (
                  <Text
                    key={index}
                    c="black"
                    size="sm"
                    style={{
                      backgroundColor: isTimeMatch ? "lightblue" : undefined,
                      padding: isTimeMatch ? "2px 4px" : undefined,
                      borderRadius: isTimeMatch ? "4px" : undefined,
                    }}
                  >
                    • {formatTime(time)}
                  </Text>
                );
              })}
            </Stack>
          ) : (
            <Text c="gray" size="sm" pl="md">
              시간 미정
            </Text>
          )}
        </Stack>

        <Stack gap="xs">
          <Text c="black" size="sm" fw={700}>
            선호 장소:
          </Text>
          {request.preferredLocations.length > 0 ? (
            <Stack gap="xs" pl="md">
              {request.preferredLocations.map((location, index) => {
                const isLocationMatch =
                  location.id === selectedStagingGroup?.selectedLocationId ||
                  (selectedExistingEvent && location.name === selectedExistingEvent.locationName);
                return (
                  <Text
                    key={index}
                    c="black"
                    size="sm"
                    style={{
                      backgroundColor: isLocationMatch ? "lightgreen" : undefined,
                      padding: isLocationMatch ? "2px 4px" : undefined,
                      borderRadius: isLocationMatch ? "4px" : undefined,
                    }}
                  >
                    • {location.name}
                  </Text>
                );
              })}
            </Stack>
          ) : (
            <Text c="gray" size="sm" pl="md">
              장소 미정
            </Text>
          )}
        </Stack>

        <Group gap="xs">
          <Button variant="outline" onClick={() => setShowAnswers(!showAnswers)} size="xs">
            {showAnswers ? "Personality Test 숨기기" : "Personality Test 보기"}
          </Button>
          {showAnswers && (
            <PersonalityAnswers
              answers={request.user.personalityTestAnswers}
              selectedQuestionIds={selectedQuestionIds}
            />
          )}

          <Button
            variant="outline"
            onClick={() => setShowPreferences(!showPreferences)}
            size="xs"
            color="teal"
          >
            {showPreferences ? "Reservation Info 숨기기" : "Reservation Info 보기"}
          </Button>

          <Button
            variant="outline"
            onClick={() => setShowEventHistory(!showEventHistory)}
            size="xs"
            color="violet"
          >
            {showEventHistory ? "Event History 숨기기" : "Event History 보기"}
          </Button>

          <Button
            variant="outline"
            onClick={handleCancel}
            loading={isCancelling}
            size="xs"
            color="red"
          >
            예약 취소
          </Button>
        </Group>

        {showPreferences && (
          <ReservationPreferencesSection
            preferredLanguage={request.preferredLanguage}
            preferredCost={request.preferredCost}
            dietaryRestrictionSlugs={request.dietaryRestrictionSlugs}
            dietaryRestrictionsOtherText={request.dietaryRestrictionsOtherText}
            messageForGuests={request.messageForGuests}
          />
        )}

        {showEventHistory && <UserEventHistory userId={request.user.uid} />}
      </Stack>
    </Card>
  );
};
